import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Key, Plus, Trash2, X, Check, Loader2, Copy, Eye, EyeOff, ToggleLeft, ToggleRight, Clock } from 'lucide-react';
import { useApiKeyList, useCreateApiKey, useUpdateApiKey, useDeleteApiKey } from '../hooks';
import { APIKey, APIKeyCreateResponse } from '../api/apiKeys';
import { copyText } from '../utils/clipboard';
import { formatDateNullable } from '../utils/date';

const ApiKeys: React.FC = () => {
  const { t } = useTranslation();
  const { data: keyData, isLoading } = useApiKeyList();
  const createKey = useCreateApiKey();
  const updateKey = useUpdateApiKey();
  const deleteKey = useDeleteApiKey();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdKey, setCreatedKey] = useState<APIKeyCreateResponse | null>(null);
  const [showCreatedKey, setShowCreatedKey] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [visibleKeyId, setVisibleKeyId] = useState<string | null>(null);

  const apiKeys = keyData?.api_keys || [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await createKey.mutateAsync({
        name: newKeyName,
        expires_at: newKeyExpiry || null,
      });
      setCreatedKey(result);
      setShowCreatedKey(true);
      setNewKeyName('');
      setNewKeyExpiry('');
    } catch (error) {
      console.error('Failed to create API key:', error);
    }
  };

  const handleToggleActive = async (apiKey: APIKey) => {
    try {
      await updateKey.mutateAsync({
        keyId: apiKey.id,
        data: { is_active: !apiKey.is_active },
      });
    } catch (error) {
      console.error('Failed to toggle API key:', error);
    }
  };

  const handleDelete = async (keyId: string) => {
    try {
      await deleteKey.mutateAsync(keyId);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    copyText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const isExpired = (expiresAt: string | null): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('apiKeys.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('apiKeys.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            setShowCreateModal(true);
            setCreatedKey(null);
            setShowCreatedKey(false);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          {t('apiKeys.newKey')}
        </button>
      </div>

      {/* Key List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin mx-auto mb-2 text-blue-500" />
            <p className="text-slate-500">{t('common.loading')}</p>
          </div>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Key size={48} className="mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">{t('apiKeys.noKeys')}</h3>
          <p className="text-slate-500 mb-6">{t('apiKeys.createFirst')}</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            {t('apiKeys.newKey')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('common.name')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">API Key</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('common.status')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('apiKeys.expiresAt')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('apiKeys.lastUsed')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('common.createdAt')}
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((apiKey) => (
                <tr key={apiKey.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Key size={14} className="text-slate-400" />
                      <span className="font-medium text-slate-800">{apiKey.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600 break-all max-w-[260px]">
                        {apiKey.key_plain && visibleKeyId === apiKey.id ? apiKey.key_plain : `${apiKey.prefix}...`}
                      </code>
                      {apiKey.key_plain && (
                        <>
                          <button
                            onClick={() => setVisibleKeyId(visibleKeyId === apiKey.id ? null : apiKey.id)}
                            className="p-1 hover:bg-slate-100 rounded text-slate-400"
                            title={visibleKeyId === apiKey.id ? t('apiKeys.hideKey') : t('apiKeys.showKey')}
                          >
                            {visibleKeyId === apiKey.id ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(apiKey.key_plain!, apiKey.id)}
                            className="p-1 hover:bg-slate-100 rounded text-slate-400"
                            title={t('apiKeys.copyKey')}
                          >
                            {copiedId === apiKey.id ? (
                              <Check size={14} className="text-green-500" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {isExpired(apiKey.expires_at) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <Clock size={10} />
                        {t('apiKeys.expired')}
                      </span>
                    ) : apiKey.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        {t('tiers.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        {t('tiers.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {apiKey.expires_at ? formatDateNullable(apiKey.expires_at) : t('apiKeys.never')}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{formatDateNullable(apiKey.last_used_at)}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{formatDateNullable(apiKey.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleToggleActive(apiKey)}
                        disabled={updateKey.isPending}
                        className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                        title={apiKey.is_active ? t('apiKeys.deactivate') : t('apiKeys.activate')}
                      >
                        {apiKey.is_active ? (
                          <ToggleRight size={18} className="text-green-500" />
                        ) : (
                          <ToggleLeft size={18} />
                        )}
                      </button>
                      {deleteConfirmId === apiKey.id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleDelete(apiKey.id)}
                            disabled={deleteKey.isPending}
                            className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            {deleteKey.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="p-1.5 border border-slate-200 rounded hover:bg-slate-50"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(apiKey.id)}
                          className="p-1.5 hover:bg-red-50 rounded text-red-500"
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg m-4">
            <div className="border-b border-slate-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">
                {createdKey ? t('apiKeys.keyCreated') : t('apiKeys.createKey')}
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-500"
              >
                <X size={20} />
              </button>
            </div>

            {createdKey ? (
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                  <Key size={20} className="text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">{t('apiKeys.keyCreatedInfo')}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-slate-100 rounded-lg text-sm font-mono break-all">
                      {showCreatedKey ? createdKey.key : '••••••••••••••••••••••••••'}
                    </code>
                    <button
                      onClick={() => setShowCreatedKey(!showCreatedKey)}
                      className="p-2 hover:bg-slate-100 rounded text-slate-500"
                    >
                      {showCreatedKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(createdKey.key, 'created')}
                      className="p-2 hover:bg-slate-100 rounded text-slate-500"
                    >
                      {copiedId === 'created' ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('apiKeys.keyName')} *</label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder={t('apiKeys.keyNamePlaceholder')}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('apiKeys.expiresAt')}</label>
                  <input
                    type="datetime-local"
                    value={newKeyExpiry}
                    onChange={(e) => setNewKeyExpiry(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <p className="text-xs text-slate-400 mt-1">{t('apiKeys.expiryHint')}</p>
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={createKey.isPending}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {createKey.isPending && <Loader2 size={16} className="animate-spin" />}
                    {t('common.create')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
